<template>
  <div>
    <div class="appui">
      <div class="appui-head">
        <div>
          <h1 class="appui-title">FAQ</h1>
          <div class="appui-crumb">App <span class="sep">/</span> FAQ</div>
        </div>
      </div>

      <!-- Vazio -->
      <div v-if="!list.length" class="appui-emptystate">
        <div class="appui-emptystate-icon"><i class="bx bx-help-circle"></i></div>
        <div class="appui-emptystate-title">Nenhuma pergunta ainda</div>
        <div class="appui-emptystate-sub">Monte o FAQ do evento — perguntas frequentes organizadas por categoria, direto no app do participante.</div>
        <button class="appui-btn appui-btn--primary" @click="openCreate()">+ Adicionar a primeira pergunta</button>
      </div>

      <!-- Bloco principal -->
      <section v-else class="appui-card appui-card--flush">
        <div class="appui-card-toolbar">
          <div class="appui-card-toolbar-left">
            <div class="appui-search">
              <i class="bx bx-search-alt"></i>
              <input v-model.trim="search" placeholder="Buscar pergunta ou resposta...">
              <button v-if="search" class="appui-search-clear" @click="search = ''" title="Limpar busca"><i class="mdi mdi-close"></i></button>
            </div>
          </div>
          <div class="appui-card-toolbar-right">
            <button class="appui-btn appui-btn--ghost" @click="categoryModal = true"><i class="mdi mdi-tag-multiple-outline mr-1"></i> Categorias</button>
            <button class="appui-btn appui-btn--primary" @click="openCreate()">+ Nova pergunta</button>
          </div>
        </div>

        <!-- Filtro por categoria (chips) -->
        <div class="appui-faq-cats">
          <button class="appui-chip" :class="{ on: categoryFilter === null }" @click="categoryFilter = null">Todas <span class="n">{{ list.length }}</span></button>
          <button
            v-for="c in categories"
            :key="c.id"
            class="appui-chip"
            :class="{ on: categoryFilter === c.id }"
            @click="categoryFilter = categoryFilter === c.id ? null : c.id"
          >{{ c.name }} <span class="n">{{ countByCategory(c.id) }}</span></button>
          <button v-if="countByCategory(0)" class="appui-chip" :class="{ on: categoryFilter === 0 }" @click="categoryFilter = categoryFilter === 0 ? null : 0">Sem categoria <span class="n">{{ countByCategory(0) }}</span></button>
        </div>

        <!-- Sem resultado -->
        <div v-if="!filtered.length" class="appui-inline-empty">
          Nenhuma pergunta encontrada. <a @click="clearFilters()">Limpar filtros</a>
        </div>

        <!-- Accordion de perguntas -->
        <div v-else>
          <div v-for="f in filtered" :key="f.id" class="appui-faq-item" :class="{ open: expanded === f.id }">
            <div class="appui-faq-q" @click="expanded = expanded === f.id ? null : f.id">
              <i class="mdi appui-faq-caret" :class="expanded === f.id ? 'mdi-chevron-down' : 'mdi-chevron-right'"></i>
              <span class="appui-faq-asking">{{ f.asking }}</span>
              <span v-if="f.category" class="appui-badge appui-badge--blue" style="flex:none">{{ f.category.name }}</span>
              <span class="appui-faq-actions" @click.stop>
                <button class="appui-tbtn" style="margin-right:6px" @click="openEdit(f)">Editar</button>
                <button class="appui-tbtn appui-tbtn--danger" @click="remove(f)">Excluir</button>
              </span>
            </div>
            <div v-if="expanded === f.id" class="appui-faq-a">{{ f.answer }}</div>
          </div>
        </div>
      </section>
    </div>

    <!-- Modal pergunta -->
    <b-modal v-model="showModal" :title="form.id ? 'Editar pergunta' : 'Nova pergunta'" size="lg" hide-footer>
      <form @submit.prevent="save()">
        <div class="form-group">
          <label>Categoria *</label>
          <select class="custom-select" v-model="form.faq_category_id">
            <option :value="null">Sem categoria</option>
            <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name }}</option>
          </select>
        </div>
        <div class="form-group"><label>Pergunta *</label><textarea class="form-control" rows="3" v-model="form.asking" required></textarea></div>
        <div class="form-group"><label>Resposta *</label><textarea class="form-control" rows="5" v-model="form.answer" required></textarea></div>
        <div class="text-right">
          <button type="button" class="btn btn-light mr-2" @click="showModal = false">Cancelar</button>
          <button type="submit" class="btn btn-primary" :disabled="saving">Salvar</button>
        </div>
      </form>
    </b-modal>

    <!-- Modal categorias (gestão: criar, editar, excluir) -->
    <b-modal v-model="categoryModal" title="Categorias do FAQ" hide-footer>
      <form class="d-flex mb-3" style="gap:8px" @submit.prevent="saveCategory()">
        <input type="text" class="form-control" v-model.trim="categoryForm.name" :placeholder="categoryForm.id ? 'Renomear categoria' : 'Nova categoria'" required>
        <button type="submit" class="btn btn-primary" style="flex:none">{{ categoryForm.id ? 'Salvar' : 'Adicionar' }}</button>
        <button v-if="categoryForm.id" type="button" class="btn btn-light" style="flex:none" @click="categoryForm = { id: null, name: '' }">Cancelar</button>
      </form>
      <table class="appui-table">
        <tbody>
          <tr v-for="c in categories" :key="c.id">
            <td class="appui-link" title="Clique para renomear" @click="categoryForm = { id: c.id, name: c.name }">{{ c.name }}</td>
            <td style="width:70px;color:#adb5bd">{{ countByCategory(c.id) }}</td>
            <td style="width:50px;text-align:right">
              <button class="appui-icon-btn appui-icon-btn--danger" title="Excluir categoria" @click="removeCategory(c)"><i class="mdi mdi-trash-can-outline"></i></button>
            </td>
          </tr>
          <tr v-if="!categories.length"><td colspan="3" class="appui-empty">Nenhuma categoria.</td></tr>
        </tbody>
      </table>
      <div class="text-right" style="margin-top:12px"><button class="btn btn-light" @click="categoryModal = false">Fechar</button></div>
    </b-modal>
  </div>
</template>

<script>
import { errorMessage, successMessage } from '@/components/alertsMessages'
import services from './services'
import './app-ui.css'

const empty = () => ({ id: null, faq_category_id: null, asking: '', answer: '' })

export default {
  components: {},
  name: 'app-faq',
  data () {
    return {
      list: [],
      categories: [],
      search: '',
      categoryFilter: null,
      expanded: null,
      showModal: false,
      saving: false,
      form: empty(),
      categoryModal: false,
      categoryForm: { id: null, name: '' }
    }
  },
  computed: {
    filtered () {
      let l = this.list
      if (this.categoryFilter === 0) l = l.filter(f => !f.faq_category_id)
      else if (this.categoryFilter != null) l = l.filter(f => f.faq_category_id === this.categoryFilter)
      if (this.search) {
        const q = this.search.toLowerCase()
        l = l.filter(f => (f.asking || '').toLowerCase().includes(q) || (f.answer || '').toLowerCase().includes(q))
      }
      return l
    }
  },
  mounted () { this.load(); this.loadCategories() },
  methods: {
    countByCategory (id) {
      if (id === 0) return this.list.filter(f => !f.faq_category_id).length
      return this.list.filter(f => f.faq_category_id === id).length
    },
    clearFilters () { this.search = ''; this.categoryFilter = null },
    load () {
      const loader = this.$loading.show()
      services.listFaqs().then(res => { this.list = res.data.data }).catch(() => errorMessage('Falha ao carregar FAQ.', 3000)).finally(() => loader.hide())
    },
    loadCategories () { services.listFaqCategories().then(res => { this.categories = res.data.data }).catch(() => {}) },
    async saveCategory () {
      if (!this.categoryForm.name) return
      try {
        if (this.categoryForm.id) await services.updateFaqCategory(this.categoryForm.id, { name: this.categoryForm.name })
        else await services.createFaqCategory({ name: this.categoryForm.name })
        this.categoryForm = { id: null, name: '' }
        this.loadCategories()
        this.load()
      } catch (e) { errorMessage('Falha ao salvar categoria.', 3000) }
    },
    async removeCategory (c) {
      const ok = await this.$bvModal.msgBoxConfirm(`Remover a categoria "${c.name}"? As perguntas ficarão sem categoria.`, { okTitle: 'Remover', cancelTitle: 'Cancelar', okVariant: 'danger' })
      if (!ok) return
      try {
        await services.deleteFaqCategory(c.id)
        if (this.categoryFilter === c.id) this.categoryFilter = null
        this.loadCategories()
        this.load()
      } catch (e) { errorMessage('Falha ao remover categoria.', 3000) }
    },
    openCreate () { this.form = empty(); this.showModal = true },
    openEdit (f) { this.form = { id: f.id, faq_category_id: f.faq_category_id, asking: f.asking, answer: f.answer }; this.showModal = true },
    async save () {
      this.saving = true
      try {
        const payload = { faq_category_id: this.form.faq_category_id || null, asking: this.form.asking, answer: this.form.answer }
        if (this.form.id) await services.updateFaq(this.form.id, payload)
        else await services.createFaq(payload)
        this.showModal = false
        successMessage('Pergunta salva.', 2000)
        this.load()
      } catch (e) { errorMessage('Falha ao salvar pergunta.', 3000) } finally { this.saving = false }
    },
    async remove (f) {
      const ok = await this.$bvModal.msgBoxConfirm('Remover esta pergunta?', { okTitle: 'Remover', cancelTitle: 'Cancelar', okVariant: 'danger' })
      if (!ok) return
      try { await services.deleteFaq(f.id); this.load() } catch (e) { errorMessage('Falha ao remover.', 3000) }
    }
  }
}
</script>
